import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Typography,
} from '@mui/material';
import { Logout as LogoutIcon } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8080';

/**
 * The thing to reach for after "was that me?".
 *
 * <p>Signing out on one machine ends that one session. This ends all of them, on every device,
 * at once, the answer to a laptop left somewhere, a phone lost, or a password just changed.
 * It takes effect on the next request rather than whenever each token happens to expire, which
 * is the part that makes it worth having a button for.
 */
const SecuritySettings = () => {
  const { logout, isDemoMode } = useAuth();
  const [working, setWorking] = useState(false);
  const [error, setError] = useState('');

  const signOutEverywhere = async () => {
    setError('');
    setWorking(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/logout-everywhere`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!response.ok) {
        setError('Could not sign out everywhere. Try again.');
        setWorking(false);
        return;
      }
      // This browser is one of the devices, so it goes too. Straight through the normal logout
      // so the app forgets the user the same way it does any other sign-out.
      await logout();
      window.location.assign('/login');
    } catch (e) {
      setError('Could not sign out everywhere. Try again.');
      setWorking(false);
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Security
      </Typography>

      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle2" gutterBottom>
            Sign out everywhere
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Ends every signed-in session, on this and any other device, straight away. Use it if
            you have left yourself signed in somewhere you no longer have, or you have just
            changed your password. You will need to sign in again here.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Button
            variant="outlined"
            color="error"
            startIcon={working ? <CircularProgress size={16} /> : <LogoutIcon />}
            disabled={working || isDemoMode}
            onClick={signOutEverywhere}
            data-testid="sign-out-everywhere"
          >
            {working ? 'Signing out…' : 'Sign out everywhere'}
          </Button>

          {isDemoMode && (
            <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
              Available once you have an account. A demo session only exists in this browser.
            </Typography>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default SecuritySettings;
